import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ConservationBenchmarkType, TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { ChartLegendBar } from '@/components/Charts';
import { CustomActiveBar } from '@/components/Charts/components/CustomActiveBar';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import { BenchmarkPill } from '@/components/Pill/ConservationPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  Bar,
  Chart,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
  ComposedChart,
  TooltipProps,
  useTimeseriesXAxisProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import {
  RenderBenchmarkReferenceLine,
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { printYear } from '@/utils/formatting/date';

const INDICATOR_SPECIES_LABEL = 'shared.ncaDetail.indicatorSpecies.evolution.statsHeading';

const chartColors = {
  indicatorSpecies: {
    bar: {
      fill: '#B9A4D5',
    },
    line: {
      stroke: '#9C90B0',
      dot: '#474D45',
    },
  },
} as const;

export type IndicatorSpeciesChartData = {
  indicatorSpeciesBar: number;
  indicatorSpeciesLine: number;
  date: Date;
  benchmark: number | null;
};

type IndicatorSpeciesChart = HTMLAttributes<HTMLDivElement> & {
  data: IndicatorSpeciesChartData[];
  benchmark: ConservationBenchmarkType | null;
  trendLabel: TrendLabelEnum | null;
} & Omit<ChartProps, 'data'>;

export const IndicatorSpeciesHistoricalChart: FC<IndicatorSpeciesChart> = ({
  data,
  benchmark,
  trendLabel,
  ...delegated
}) => {
  const { t } = useTranslation();

  const xAxisProps = useTimeseriesXAxisProps(data);
  const benchmarks = useGetConservationBenchmarks();

  return (
    <>
      <Chart.Container
        {...delegated}
        data={data}
        config={{
          indicatorSpeciesBar: {
            label: t(INDICATOR_SPECIES_LABEL),
            color: chartColors.indicatorSpecies.bar.fill,
          },
          indicatorSpeciesLine: {
            label: t(INDICATOR_SPECIES_LABEL),
            color: chartColors.indicatorSpecies.line.stroke,
          },
        }}
        data-testid='indicator-species-chart'
      >
        <ComposedChart data={data}>
          <Bar
            isAnimationActive={false}
            fill={chartColors.indicatorSpecies.bar.fill}
            dataKey='indicatorSpeciesBar'
            activeBar={<CustomActiveBar fill={chartColors.indicatorSpecies.bar.fill} />}
            barSize={20}
            className='indicator-species-bar'
          />
          {RenderTrendLine({
            dataKey: 'indicatorSpeciesLine',
            stroke: chartColors.indicatorSpecies.line.stroke,
            label: (props) =>
              props.index === data.length - 1 && trendLabel ? (
                <TrendLabel
                  trend={trendLabel}
                  lineColor={chartColors.indicatorSpecies.line.stroke}
                  strokeWidth={2}
                  infoPopoverProps={{
                    title: t(`shared.projects.project.conservation.trendLabel.${trendLabel}`),
                    body: t(`shared.projects.project.conservation.explainers.tooltip.trendLabel.${trendLabel}`),
                  }}
                  {...props}
                />
              ) : (
                <></>
              ),
            dotFill: chartColors.indicatorSpecies.line.dot,
          })}
          {benchmark &&
            RenderBenchmarkReferenceLine({
              value: benchmarks[`${benchmark}`],
              benchmark,
              infoPopoverProps: {
                title: t(`shared.projects.project.conservation.benchmark.${benchmark}`),
                body: t(`shared.projects.project.conservation.explainers.tooltip.benchmark.${benchmark}`),
              },
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='indicator-species-x-axis' />
          <Chart.YAxis domain={minMaxAxisDomain(0.98, 1.02)} className='indicator-species-y-axis'>
            <Chart.YAxisLabel>{t('shared.ncaDetail.indicatorSpecies.evolution.value')}</Chart.YAxisLabel>
          </Chart.YAxis>
          <Chart.Tooltip cursor={false} content={(props) => <CustomTooltip benchmarks={benchmarks} {...props} />} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(INDICATOR_SPECIES_LABEL),
      color: chartColors.indicatorSpecies.bar.fill,
      shape: 'column',
    },
    {
      label: t('shared.ncaDetail.indicatorSpecies.evolution.trend'),
      color: chartColors.indicatorSpecies.line.stroke,
      shape: 'line',
    },
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='indicator-species-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
}: TooltipProps<ValueType, NameType> & { benchmarks: Record<ConservationBenchmarkType, number> }) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const linePayload = payload.find((data) => data.dataKey === 'indicatorSpeciesLine');
  const indicatorSpeciesDisplayValue =
    linePayload?.value !== undefined && linePayload?.value !== null
      ? getDisplayNumber(linePayload.value as string, window.navigator.language)
      : t('global.analysis.noData');

  const benchmark = getBenchmarkForConservationFact({
    fact: { value: linePayload?.value as number | null },
    benchmarks,
  });

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {benchmark && <BenchmarkPill className='typography-body2' benchmark={benchmark} />}
        <Stack spacing={8} direction='row'>
          <ChartTooltipTextPrimary>{t(INDICATOR_SPECIES_LABEL)}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{indicatorSpeciesDisplayValue}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { CustomTooltip as CustomTooltipIndicatorSpecies };
